import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Card,
  CardContent,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Divider,
  LinearProgress,
  CircularProgress,
  Alert,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  TextField,
  IconButton
} from '@mui/material';
import {
  ExpandMore,
  SearchOutlined,
  ClearOutlined,
  InfoOutlined,
  VerifiedUser,
  PeopleOutline
} from '@mui/icons-material';
import { PieChart, Pie, Cell, Tooltip as RechartsTooltip } from 'recharts';

const UARCACheckSummary = ({ uarFile, appRecord, userswithSOD, rolesForReview, chosenRoles, hrList }) => {
  const [loading, setLoading] = useState(true);
  const [roleStats, setRoleStats] = useState({});
  const [userStats, setUserStats] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [expandedRoles, setExpandedRoles] = useState([]);
  const [userDetailsMap, setUserDetailsMap] = useState(new Map());

  // Colors for charts
  const COLORS = ['#1976d2', '#4caf50', '#f44336', '#ff9800'];

  // Process data when relevant props change
  useEffect(() => {
    if (appRecord && userswithSOD) { 
      const emailMap = new Map();
      
      if (Array.isArray(appRecord)) {
        appRecord.forEach(user => {
          if (user.EMAIL_ADDRESS) {
            emailMap.set(user.EMAIL_ADDRESS.toLowerCase(), user);
          }
        });
      }
      
      setUserDetailsMap(emailMap);
      
      processData();
    }
  }, [appRecord, userswithSOD, chosenRoles, rolesForReview]);

  // Set up search functionality
  useEffect(() => {
    if (userswithSOD && userswithSOD.length > 0) {
      if (searchTerm.trim() === '') {
        setFilteredUsers(userswithSOD);
      } else {
        const term = searchTerm.toLowerCase();
        const filtered = userswithSOD.filter(user => 
          (user.FIRST_NAME && user.FIRST_NAME.toLowerCase().includes(term)) ||
          (user.LAST_NAME && user.LAST_NAME.toLowerCase().includes(term)) ||
          (user.EMAIL_ADDRESS && user.EMAIL_ADDRESS.toLowerCase().includes(term)) ||
          (user.ROLE_NAME && user.ROLE_NAME.toLowerCase().includes(term))
        );
        setFilteredUsers(filtered);
      }
    }
  }, [searchTerm, userswithSOD]);

  // Process data to generate statistics
  const processData = () => {
    setLoading(true);
    
    try {
      // Create role statistics
      const roles = {};
      const chosenRoleNames = chosenRoles ? chosenRoles.map(r => r.ROLE_NAME) : [];
      
      // Process users by role for statistics
      userswithSOD.forEach(user => {
        const roleName = user.ROLE_NAME || 'Unknown';
        
        if (!roles[roleName]) {
          roles[roleName] = {
            name: roleName,
            totalUsers: 0,
            isInScope: chosenRoleNames.includes(roleName)
          };
        }
        
        roles[roleName].totalUsers++;
      });
      
      // Add any chosen roles that might not have users yet
      chosenRoleNames.forEach(roleName => {
        if (!roles[roleName]) {
          roles[roleName] = {
            name: roleName,
            totalUsers: 0,
            isInScope: true
          };
        }
      });
      
      // Process user statistics
      const userCounts = {
        total: userswithSOD.length,
        byRole: Object.values(roles).map(role => ({
          name: role.name,
          value: role.totalUsers,
          isInScope: role.isInScope
        })),
        inscope: Object.values(roles).filter(r => r.isInScope).reduce((sum, role) => sum + role.totalUsers, 0)
      };
      
      // Calculate roles statistics
      const roleCounts = {
        total: Object.keys(roles).length,
        inscope: chosenRoleNames.length,
        outofscope: Object.keys(roles).length - chosenRoleNames.length,
        data: [
          { name: 'In Scope', value: chosenRoleNames.length },
          { name: 'Out of Scope', value: Object.keys(roles).length - chosenRoleNames.length }
        ]
      };
      
      setRoleStats(roleCounts);
      setUserStats(userCounts);
      setFilteredUsers(userswithSOD);
      setLoading(false);
    } catch (error) {
      console.error('Error processing data:', error);
      setLoading(false);
    }
  };

  // Handle role accordion expansion
  const handleRoleExpand = (roleName) => {
    if (expandedRoles.includes(roleName)) {
      setExpandedRoles(expandedRoles.filter(r => r !== roleName));
    } else {
      setExpandedRoles([...expandedRoles, roleName]);
    }
  };

  // Check if a specific role is expanded
  const isRoleExpanded = (roleName) => {
    return expandedRoles.includes(roleName);
  };

  // Get users for a specific role
  const getUsersForRole = (roleName) => {
    return userswithSOD.filter(user => user.ROLE_NAME === roleName);
  };

  // Get user display name using the userDetailsMap
  const getUserDisplayName = (user) => {
    if (!user) return 'Unknown User';
    const userDetails = userDetailsMap.get(user.EMAIL_ADDRESS?.toLowerCase());
    if (userDetails && (userDetails.FIRST_NAME || userDetails.LAST_NAME)) {
      return `${userDetails.FIRST_NAME || ''} ${userDetails.LAST_NAME || ''}`.trim();
    }
    if (user.FIRST_NAME || user.LAST_NAME) {
       return `${user.FIRST_NAME || ''} ${user.LAST_NAME || ''}`.trim();
    }
    return user.EMAIL_ADDRESS || 'Unknown Email';
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', my: 4 }}>
        <CircularProgress size={40} />
        <Typography variant="body2" sx={{ mt: 2 }}>
          Loading data for completeness check...
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      <Card sx={{ mb: 3, border: '1px solid', borderColor: 'divider' }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <InfoOutlined color="primary" sx={{ mr: 1 }} />
            <Typography variant="h6">
              Completeness & Accuracy Check Instructions
            </Typography>
          </Box>
          
          <Typography variant="body2" color="text.secondary" paragraph>
            Review the summary information below to verify that all necessary users and roles are included in the User Access Review.
            You should check that:
          </Typography>
          
          <Box component="ul" sx={{ pl: 2, mt: 1, color: 'text.secondary' }}>
            <li>
              <Typography variant="body2">
                All in-scope roles have been properly selected for review
              </Typography>
            </li>
            <li>
              <Typography variant="body2">
                The user count for each role matches your expectations
              </Typography>
            </li>
            <li>
              <Typography variant="body2">
                All users assigned to those roles are included in the review list
              </Typography>
            </li>
            <li>
              <Typography variant="body2">
                There are no unexpected users or missing users in the review list
              </Typography>
            </li>
            <li>
              <Typography variant="body2">
                The user and role information matches system of record data (compare with the source system)
              </Typography>
            </li>
          </Box>
          
          <Typography variant="body2" color="text.secondary" paragraph sx={{ mt: 2 }}>
            After verifying the data, you must upload evidence of your completeness and accuracy check
            before proceeding to the SOD review step. <strong>Important:</strong> Your evidence should validate 
            that the generated list matches the data within the system of record. This can include screenshots, 
            reports, or queries from the source system that prove the system records tie out exactly with this 
            generated list. Any discrepancies should be documented and explained.
          </Typography>
        </CardContent>
      </Card>
      
      <Grid container spacing={3} sx={{ mb: 3 }}>
        {/* Role statistics */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, height: '100%', borderRadius: 2 }}>
            <Typography variant="h6" gutterBottom>
              Role Statistics
            </Typography>
            
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={4}>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="h4" color="primary" sx={{ fontWeight: 500 }}>
                    {roleStats.total || 0}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total Roles
                  </Typography>
                </Box>
              </Grid>
              
              <Grid item xs={4}>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="h4" color="success.main" sx={{ fontWeight: 500 }}>
                    {roleStats.inscope || 0}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    In Scope
                  </Typography>
                </Box>
              </Grid>
              
              <Grid item xs={4}>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="h4" color="text.secondary" sx={{ fontWeight: 500 }}>
                    {roleStats.outofscope || 0}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Out of Scope
                  </Typography>
                </Box>
              </Grid>
            </Grid>
            
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
              <PieChart width={200} height={200}>
                <Pie
                  data={roleStats.data || []}
                  cx={100}
                  cy={100}
                  innerRadius={60}
                  outerRadius={80}
                  fill="#8884d8"
                  paddingAngle={2}
                  dataKey="value"
                  label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                >
                  {roleStats.data?.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <RechartsTooltip />
              </PieChart>
            </Box>
          </Paper>
        </Grid>
        
        {/* User statistics */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, height: '100%', borderRadius: 2 }}>
            <Typography variant="h6" gutterBottom>
              User Statistics
            </Typography>
            
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={6}>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="h4" color="primary" sx={{ fontWeight: 500 }}>
                    {userStats.total || 0}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total User-Role Assignments
                  </Typography>
                </Box>
              </Grid>
              
              <Grid item xs={6}>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="h4" color="success.main" sx={{ fontWeight: 500 }}>
                    {userStats.inscope || 0}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    In-Scope Assignments
                  </Typography>
                </Box>
              </Grid>
            </Grid>
            
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                Completion Progress
              </Typography>
              <LinearProgress
                variant="determinate"
                value={userStats.total ? (userStats.inscope / userStats.total) * 100 : 0}
                sx={{ height: 8, borderRadius: 4 }}
              />
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                {userStats.inscope} of {userStats.total} user-role assignments will be reviewed ({userStats.total ? Math.round((userStats.inscope / userStats.total) * 100) : 0}%)
              </Typography>
            </Box>
          </Paper>
        </Grid>
      </Grid>
      
      {/* Search bar */}
      <Box sx={{ mb: 3 }}>
        <TextField
          fullWidth
          placeholder="Search users by name, email, or role..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          size="small"
          variant="outlined"
          InputProps={{
            startAdornment: (
              <SearchOutlined sx={{ color: 'action.active', mr: 1 }} />
            ),
            endAdornment: searchTerm && (
              <IconButton
                size="small"
                onClick={() => setSearchTerm('')}
                sx={{ visibility: searchTerm ? 'visible' : 'hidden' }}
              >
                <ClearOutlined fontSize="small" />
              </IconButton>
            ),
          }}
          sx={{ bgcolor: 'background.paper' }}
        />
      </Box>
      
      {/* User list by role */}
      <Paper sx={{ p: 3, mb: 3, borderRadius: 2 }}>
        <Typography variant="h6" gutterBottom>
          Users by Role
        </Typography>
        
        {userStats.byRole && userStats.byRole
          .sort((a, b) => (a.isInScope === b.isInScope ? 0 : a.isInScope ? -1 : 1))
          .map((role, index) => (
            <Accordion 
              key={index}
              expanded={isRoleExpanded(role.name)}
              onChange={() => handleRoleExpand(role.name)}
              sx={{
                mb: 1,
                boxShadow: 'none',
                border: '1px solid',
                borderColor: 'divider',
                '&:before': { display: 'none' },
                backgroundColor: role.isInScope ? 'rgba(76, 175, 80, 0.04)' : 'transparent'
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMore />}
                sx={{ backgroundColor: role.isInScope ? 'rgba(76, 175, 80, 0.08)' : 'rgba(0, 0, 0, 0.02)' }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', pr: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <PeopleOutline sx={{ mr: 1, color: role.isInScope ? 'success.main' : 'text.secondary' }} />
                    <Typography sx={{ fontWeight: role.isInScope ? 500 : 400 }}>
                      {role.name}
                    </Typography>
                    <Chip 
                      size="small" 
                      label={role.isInScope ? 'In Scope' : 'Out of Scope'} 
                      color={role.isInScope ? 'success' : 'default'}
                      variant={role.isInScope ? 'outlined' : 'outlined'}
                      sx={{ ml: 2 }}
                    />
                  </Box>
                  <Box>
                    <Typography sx={{ fontWeight: 500 }}>
                      {role.value} user{role.value !== 1 ? 's' : ''}
                    </Typography>
                  </Box>
                </Box>
              </AccordionSummary>
              
              <AccordionDetails sx={{ p: 0 }}>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ backgroundColor: 'rgba(0, 0, 0, 0.02)' }}>
                        <TableCell sx={{ fontWeight: 'bold' }}>Name</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>Email</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>Role Owner</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {getUsersForRole(role.name).map((user, userIndex) => {
                        // Directly use the new fields from the user object (UAR_SOD_CHECK data)
                        const roleOwnerName = (user.ROLE_OWNER_FIRST_NAME || user.ROLE_OWNER_LAST_NAME) 
                          ? `${user.ROLE_OWNER_FIRST_NAME || ''} ${user.ROLE_OWNER_LAST_NAME || ''}`.trim()
                          : (user.ROLE_OWNER ? 'Owner Details Missing' : 'Not Assigned'); // Check if ROLE_OWNER exists

                        // Get user display name using the map/fallbacks
                        const userDisplayName = getUserDisplayName(user);
                        
                        return (
                          <TableRow key={userIndex} hover>
                            <TableCell>
                              {userDisplayName}
                            </TableCell>
                            <TableCell>{user.EMAIL_ADDRESS}</TableCell>
                            <TableCell>{roleOwnerName}</TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              </AccordionDetails>
            </Accordion>
          ))}
      </Paper>
      
      {/* All users list */}
      <Paper sx={{ p: 3, borderRadius: 2 }}>
        <Typography variant="h6" gutterBottom>
          All Users for Review {filteredUsers.length > 0 && `(${filteredUsers.length})`}
        </Typography>
        
        {filteredUsers.length === 0 && (
          <Alert severity="info" sx={{ mb: 2 }}>
            No users found with the applied filters.
          </Alert>
        )}
        
        {filteredUsers.length > 0 && (
          <TableContainer sx={{ maxHeight: 400 }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold' }}>#</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Name</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Email</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Role</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Role Owner</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>SOD Conflict</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredUsers.map((user, index) => {
                  // Directly use the new fields from the user object (UAR_SOD_CHECK data)
                  const roleOwnerName = (user.ROLE_OWNER_FIRST_NAME || user.ROLE_OWNER_LAST_NAME) 
                    ? `${user.ROLE_OWNER_FIRST_NAME || ''} ${user.ROLE_OWNER_LAST_NAME || ''}`.trim()
                    : (user.ROLE_OWNER ? 'Owner Details Missing' : 'Not Assigned'); // Check if ROLE_OWNER exists

                   // Get user display name using the map/fallbacks
                  const userDisplayName = getUserDisplayName(user);
                  
                  return (
                    <TableRow key={index} hover>
                      <TableCell>{index + 1}</TableCell>
                      <TableCell>
                        {userDisplayName}
                      </TableCell>
                      <TableCell>{user.EMAIL_ADDRESS}</TableCell>
                      <TableCell>{user.ROLE_NAME}</TableCell>
                      <TableCell>{roleOwnerName}</TableCell>
                      <TableCell>
                        <Chip 
                          label={user.WITH_SOD_VIOLATION ? 'Yes' : 'No'} 
                          size="small"
                          color={user.WITH_SOD_VIOLATION ? 'error' : 'success'}
                          variant={user.WITH_SOD_VIOLATION ? 'filled' : 'outlined'}
                        />
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>
    </Box>
  );
};

export default UARCACheckSummary; 